import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { creators as BalanceSheetActions } from '../../../Reducers/Reporting/BalanceSheet';
import { formatedStyle } from '../../../Assets/js/constant';
import ManageReportDataApi from '../../../Api/ApiManageReportData';
import TopChart from './TopChart';
import MiddleChart from './MiddleChart';
import BottomChart from './BottomChart';

function BalanceSheet({ selectedCompany, defaultDimDate, bubbleFlag }) {
  const [accountStructure, setAccountStructure] = useState([]);
  const dispatch = useDispatch();

  const balanceSheetSummary = useSelector(
    (state) => state?.Reporting?.Summary?.reportingSummaries?.balanceSheet ?? '',
  );
  const queryResult = useSelector(
    (state) => state?.Reporting?.BalanceSheet?.queryResult ?? {},
  );
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const fetchAccountStructure = async () => {
    try {
      const res = await ManageReportDataApi.getTemplate('BalanceSheetLayout');
      if (res.status === 200) {
        setAccountStructure(res.data);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchAccountStructure();
  }, [selectedCompany]);

  const triggerBalanceSheetRequest = useCallback(() => {
    dispatch(BalanceSheetActions.glbsQuerysRequest(defaultDimDate));
  }, [dispatch, defaultDimDate]);

  useEffect(() => {
    if (!lastFetchedCompany || !lastFetchedDimDate) {
      triggerBalanceSheetRequest();
    }
  }, [
    defaultDimDate,
    lastFetchedCompany,
    lastFetchedDimDate,
    triggerBalanceSheetRequest,
  ]);

  useEffect(() => {
    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;
    
    if (
      valuesChanged &&
      lastFetchedCompany &&
      lastFetchedDimDate
    ) {
      triggerBalanceSheetRequest();
    }
  }, [defaultDimDate, selectedCompany, lastFetchedCompany, lastFetchedDimDate, triggerBalanceSheetRequest]);


  const currentRatio = isNaN(queryResult.CurrentRatio)
    ? 0
    : Number(queryResult.CurrentRatio);
  // const kpiTarget = isNaN(queryResult.KpiTarget)
  //   ? 0
  //   : Number(queryResult.KpiTarget);
  const roi = isNaN(queryResult.Roi)
    ? 0
    : Math.round(Number(queryResult.Roi) * 100);

  return (
    <div style={{ height: '89vh' }}>
      <p
        style={{
          ...formatedStyle(-1, 12, 0, 1),
          marginTop: 8,
          float: 'right',
          color: '#25aae3',
        }}
        align="right"
        className="grayHover"
      >
        Current Ratio:&nbsp;&nbsp;&nbsp;&nbsp;
        {currentRatio > 0
          ? Math.round(Math.round(currentRatio * 10) / 10)
          : `(${Math.round(Math.abs(currentRatio) * 10) / 10})`}
        &nbsp;:&nbsp;1
        {/* {kpiTarget > 0 ? kpiTarget : `(${Math.abs(kpiTarget)})`} */}
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;ROI:&nbsp;&nbsp;&nbsp;&nbsp;
        {isFinite(roi) ? (roi > 0 ? `${roi}%` : `(${Math.abs(roi)}%)`) : '0%'}
      </p>

      <div>
        <TopChart
          queryData={queryResult}
          accountStructure={accountStructure[0]}
        />
        <MiddleChart
          queryData={queryResult}
          accountStructure={accountStructure[1]}
        />
        <BottomChart
          queryData={queryResult}
          accountStructure={accountStructure[2]}
        />
      </div>

      {bubbleFlag && balanceSheetSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed', 
            bottom: 60, 
            right: 30, 
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={balanceSheetSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
}

BalanceSheet.propTypes = {
  selectedCompany: PropTypes.number.isRequired,
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
};

export default BalanceSheet;
